import React from 'react';
import MUIDataTableImport, { DisplayData, MUIDataTableColumnDef, MUIDataTableProps } from 'mui-datatables';

/** mui-datatables 类组件与 React 18 @types/react 的 JSX 校验不兼容（TS2786） */
const MUIDataTable = MUIDataTableImport as unknown as React.ComponentType<MUIDataTableProps>;
import { Box, Checkbox, CircularProgress } from '@mui/material';
import i18n from '../../i18n';
import '../../page/dish-menu-creator/index.scss';
import './scss/mui-datatable-wrapper.scss';

/** mui-datatables 注入的 Checkbox 透传（含 `sx` 与行选 data-*，`data-index` 可能为 null） */
type DataTableCheckboxProps = React.ComponentProps<typeof Checkbox> & {
    'data-description'?: string;
    'data-index'?: number | null;
};

export default class Table extends React.Component<Props, State> {
    public baseTableRefs: React.RefObject<any> = React.createRef();
    render() {
        const { download, filter, print, search, viewColumns, pagination, selectableRowsHeader, rowsSelected, selectableRowsHideCheckboxes, count,
            title, data, columns, onCellClick, onChangePage, onChangeRowsPerPage, setRowProps, elevation, otherOption, loading, onTableChange,
            rowsPerPage, page, onRowSelectionChange, selectToolbarPlacement, customToolbarSelect, selectableRowsOnClick, disableCheckBoxRow,
            filterType, centerCellContent } = this.props;
        const rootClassName = ['MUIDataTable', centerCellContent ? 'MUIDataTable--center-cells' : ''].filter(Boolean).join(' ');
        /** 未选中图标色：设计 token「图标-按钮/辅助色-300」→ `--icon-neutral-300` */
        const CustomCheckbox = (props: DataTableCheckboxProps) => {
            const incomingSx = props.sx;
            const checkboxRest = { ...props };
            delete checkboxRest.sx;
            const checkboxDisabled =
                checkboxRest['data-description'] === 'row-select' &&
                !!disableCheckBoxRow?.includes(Number(checkboxRest['data-index']) || 0);
            const sxList = [
                {
                    /** 未选中：图标-按钮/辅助色-300 */
                    color: 'var(--icon-neutral-300)',
                    /** 选中 / 半选：保持主题勾选色 */
                    '&.Mui-checked': { color: 'var(--icon-primary-300)' },
                    '&.MuiCheckbox-indeterminate': { color: 'var(--icon-primary-300)' },
                },
                ...(incomingSx ? (Array.isArray(incomingSx) ? incomingSx : [incomingSx]) : []),
            ];
            return <Checkbox {...checkboxRest} disabled={checkboxDisabled} sx={sxList} />;
        };
        return (
            <div className={rootClassName}>
                <MUIDataTable
                    innerRef={this.baseTableRefs}
                    title={title}
                    data={data}
                    columns={columns}
                    options={{
                        responsive: 'standard',
                        filterType: filterType ?? 'checkbox',
                        download: download ?? false,
                        filter: filter ?? false,
                        print: print ?? false,
                        search: search ?? false,
                        viewColumns: viewColumns ?? false,
                        pagination: pagination ?? false,
                        rowsSelected,
                        selectableRowsHeader: selectableRowsHeader ?? false,
                        selectableRowsHideCheckboxes: selectableRowsHideCheckboxes ?? true,
                        page,
                        rowsPerPage: rowsPerPage ?? 20,
                        elevation: elevation ?? 2,
                        rowsPerPageOptions: [20, 50, 100],
                        count,
                        selectToolbarPlacement: selectToolbarPlacement as any ?? 'none', // 隐藏选中框 选中后 -> 显示提示删除列
                        onCellClick,
                        onChangePage,
                        onChangeRowsPerPage,
                        onTableChange,
                        onRowSelectionChange,  // 选中框回调方法
                        textLabels: {
                            body: {
                                noMatch: i18n.t('google_text_public_text_no_data')
                            },
                            pagination: {
                                next: i18n.t('google_text_next_page'),
                                previous: i18n.t('google_text_previous_page'),
                                rowsPerPage: i18n.t('google_text_few_row_each_page'),
                                displayRows: 'of',

                            },

                        },
                        selectableRowsOnClick: selectableRowsOnClick ?? false,
                        customToolbarSelect,

                        setRowProps,
                        onDownload: (buildHead, buildBody, columns, data) => {
                            return '\uFEFF' + buildHead(columns) + buildBody(data);
                        },
                        ...otherOption,
                    }}
                    components={{
                        Checkbox: CustomCheckbox,
                    }}
                />
                {loading && <Box className='table-loading' sx={(theme) => { return { background: `${theme.palette.background.default}90` } }}>
                    <CircularProgress color='primary' />
                </Box>}
            </div>
        )
    }
}

// **************
// TYPE
// **************

type State = {};

type Props = {
    data: any;
    columns: MUIDataTableColumnDef[];
    title?: string | React.ReactNode;
    download?: boolean; // 下载
    filter?: boolean; // 筛选
    print?: boolean; // 打印
    search?: boolean; // 搜索
    viewColumns?: boolean; // 显示列
    pagination?: boolean; //  分页
    selectableRowsHeader?: boolean; // 全选
    selectableRowsHideCheckboxes?: boolean; // 选中框
    onCellClick?: (colData: any, cellMeta: { colIndex: number; rowIndex: number; dataIndex: number; event: React.MouseEvent }) => void;  // 点击行的事件
    onChangePage?: (currentPage: number) => void;  // 页面更改触发事件
    onChangeRowsPerPage?: (numberOfRows: number) => void;  // 页面行数更改触发事件
    setRowProps?: (row: any[], dataIndex: number, rowIndex: number) => object;
    elevation?: number; // 阴影
    otherOption?: MUIDataTableProps['options'];
    loading?: boolean;
    count?: number; // 表格总数
    selectToolbarPlacement?: string; // 隐藏选中框 选中后 -> 显示提示删除列
    onTableChange?: (action: any, tableState: any) => void;
    rowsPerPage?: number; // 每页的行数
    page?: number;   // 页数
    onRowSelectionChange?: (currentRowsSelected: any[], allRowsSelected: any[], rowsSelected?: any[]) => void;
    rowsSelected?: number[],
    customToolbarSelect?: (
        selectedRows: { data: Array<{ index: number; dataIndex: number }>; lookup: { [key: number]: boolean } },
        displayData: DisplayData,
        setSelectedRows: (rows: number[]) => void,
    ) => React.ReactNode;
    selectableRowsOnClick?: boolean; // 单击行时选择切换
    disableCheckBoxRow?: number[];// 禁用行 checkBox

    filterType?: 'checkbox' | 'dropdown' | 'multiselect' | 'textField' | 'custom';
    /** 为 true 时表头、表体（含多选列）内容水平居中，适配 mui-datatables 内部 Button/flex 结构 */
    centerCellContent?: boolean;
};
